import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Users, BellRing, TrendingUp, Search, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminEnrollments, getAdminEnrollmentStats, updateAdminEnrollment } from '@/lib/api';

const STATUS_META = {
  new:       { label: 'New',       color: 'bg-slate-100 text-slate-700' },
  contacted: { label: 'Contacted', color: 'bg-sky-100 text-sky-700' },
  quoted:    { label: 'Quoted',    color: 'bg-amber-100 text-amber-700' },
  won:       { label: 'Won',       color: 'bg-emerald-100 text-emerald-700' },
  lost:      { label: 'Lost',      color: 'bg-rose-100 text-rose-700' },
};
const STATUS_KEYS = Object.keys(STATUS_META);

const SOURCE_META = {
  erp_lead:        { label: 'ERP Lead',        color: 'bg-violet-100 text-violet-700',  icon: Building2 },
  team_enrollment: { label: 'Team Enrollment', color: 'bg-indigo-100 text-indigo-700',  icon: Users },
  aws_waitlist:    { label: 'AWS Waitlist',    color: 'bg-amber-100 text-amber-700',    icon: BellRing },
};

const StatCard = ({ icon: Icon, label, value, iconColor }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AdminEnrollments() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {row, status, notes}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [{ data: st }, { data: en }] = await Promise.all([
        getAdminEnrollmentStats(),
        getAdminEnrollments(params),
      ]);
      setStats(st);
      setRows(en.results || []);
    } catch (err) {
      toast.error('Could not load enrollments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [sourceFilter, statusFilter]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.org || '').toLowerCase().includes(q) ||
      (r.cert_or_interest || '').toLowerCase().includes(q)
    );
  });

  const openEdit = (row) => setEditing({ ...row, notes: row.notes || '' });

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await updateAdminEnrollment(editing.source, editing.id, {
        status: editing.status,
        notes: editing.notes || null,
      });
      toast.success('Enrollment updated');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-enrollments">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Enrollment Inquiries</h1>
        <p className="text-slate-500 mt-1">All ERP leads, team enrollments, and AWS Waitlist signups in one pipeline.</p>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Inquiries" value={stats?.total ?? '—'} iconColor="bg-slate-100 text-slate-700" />
        <StatCard icon={Building2} label="ERP Leads" value={stats?.erp_lead ?? '—'} iconColor="bg-violet-100 text-violet-700" />
        <StatCard icon={Users} label="Team Enrollments" value={stats?.team_enrollment ?? '—'} iconColor="bg-indigo-100 text-indigo-700" />
        <StatCard icon={TrendingUp} label="Team Pipeline / Mo" value={`$${(stats?.team_pipeline_value_monthly ?? 0).toLocaleString()}`} iconColor="bg-emerald-100 text-emerald-700" />
      </div>

      {/* FILTERS */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search name, email, org…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="enrollments-search"
              />
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger data-testid="enrollments-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="erp_lead">ERP Lead</SelectItem>
                <SelectItem value="team_enrollment">Team Enrollment</SelectItem>
                <SelectItem value="aws_waitlist">AWS Waitlist</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="enrollments-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_KEYS.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="enrollments-refresh">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</> : 'Refresh'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* TABLE */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Org / Track</TableHead>
                  <TableHead>Seats / Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No inquiries match your filters.</TableCell></TableRow>
                )}
                {filtered.map((r) => {
                  const src = SOURCE_META[r.source];
                  const SrcIcon = src?.icon || Users;
                  const st = STATUS_META[r.status] || STATUS_META.new;
                  const date = new Date(r.created_at);
                  return (
                    <TableRow key={`${r.source}-${r.id}`} data-testid={`enrollment-row-${r.source}-${r.id}`}>
                      <TableCell>
                        <Badge className={src?.color || 'bg-slate-100 text-slate-700'}>
                          <SrcIcon className="w-3 h-3 mr-1" /> {src?.label || r.source}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{r.name || '—'}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-slate-900 max-w-[220px] truncate" title={r.cert_or_interest}>{r.cert_or_interest}</div>
                        {r.org && <div className="text-xs text-slate-500 max-w-[220px] truncate">{r.org}</div>}
                      </TableCell>
                      <TableCell>
                        {r.seats ? <div className="text-slate-900">{r.seats} seats</div> : <div className="text-slate-400">—</div>}
                        {r.amount ? <div className="text-xs text-emerald-700 font-medium">${r.amount.toLocaleString()}/mo</div> : null}
                      </TableCell>
                      <TableCell><Badge className={st.color}>{st.label}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`enrollment-edit-${r.id}`}>Update</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="enrollment-edit-dialog">
          <DialogHeader>
            <DialogTitle>Update inquiry</DialogTitle>
            <DialogDescription>
              {editing?.name} · <span className="text-slate-500">{editing?.email}</span>
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-slate-800">Track:</span> {editing.cert_or_interest}
                {editing.seats ? ` · ${editing.seats} seats` : ''}
                {editing.amount ? ` · $${editing.amount.toLocaleString()}/mo` : ''}
              </div>
              {editing.message && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-600">
                  <p className="font-medium text-xs uppercase text-slate-400 mb-1">Message from prospect</p>
                  {editing.message}
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-slate-700">Status</label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="enrollment-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_KEYS.map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Internal notes</label>
                <Textarea
                  className="mt-1"
                  rows={4}
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  placeholder="Follow-up date, quote sent, decision-maker context…"
                  data-testid="enrollment-edit-notes"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="enrollment-edit-save">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
